import React, { MouseEvent } from 'react'
import useActiveOrDefaultChain from 'src/common/hooks/web3/useActiveOrDefaultChain'
import Button, { ButtonProps } from 'src/common/ui-kit/Button/Button'
import {
  useActiveAccount,
  useActiveWalletChain,
  useActiveWalletConnectionStatus,
  useSwitchActiveWalletChain,
} from 'thirdweb/react'
import { useSetAtom } from 'jotai'
import {
  kycModalAtom,
} from 'src/pages/TradePage/stores/trade-store'
import useTier2 from 'src/common/queries/useTier2'
import { useConnectWallet } from 'src/common/hooks/web3/useConnectWallet'

export interface SmartButtonProps extends ButtonProps {
  requireAccount?: boolean
  requireChain?: boolean
  requireTier2?: boolean
}

const SmartButton: React.FC<SmartButtonProps> = ({
  children,
  requireAccount = true,
  requireChain = true,
  requireTier2 = true,
  onClick,
  disabled: disabledProp,
  loading,
  ...rest
}) => {
  const account = useActiveAccount()
  const chain = useActiveWalletChain()
  const desiredChain = useActiveOrDefaultChain()
  const { connect } = useConnectWallet()
  const connectStatus = useActiveWalletConnectionStatus()
  const isWalletLoading = connectStatus === 'connecting'
  const switchNetwork = useSwitchActiveWalletChain()
  const { data: isTier2, isFetching: isTier2Fetching } = useTier2()

  loading =
    loading ||
    isWalletLoading ||
    isTier2Fetching ||
    (isTier2 === null && Boolean(account?.address))
  const setKycModal = useSetAtom(kycModalAtom)

  const accountIsRequired = requireAccount && !account
  const chainIsRequired = requireChain && chain?.id !== desiredChain.id
  const disabled =
    (!accountIsRequired &&
      !chainIsRequired &&
      Boolean(isTier2) &&
      disabledProp) ||
    loading

  const getButtonChildren = () => {
    if (accountIsRequired) {
      return 'Connect'
    }

    if (chainIsRequired) {
      return 'Switch network'
    }

    if (requireTier2 && !isTier2) {
      return 'Enable account'
    }

    return children
  }

  const handleClick = async (e: MouseEvent<HTMLButtonElement>) => {
    if (accountIsRequired) {
      connect()
      return
    }
    if (chainIsRequired) {
      switchNetwork(desiredChain)
      return
    }

    if (requireTier2 && !isTier2) {
      setKycModal(true)
      return
    }

    onClick?.(e)
  }

  return (
    <Button
      onClick={handleClick}
      disabled={disabled}
      loading={loading}
      {...rest}
    >
      {getButtonChildren()}
    </Button>
  )
}

export default SmartButton
